import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, AlertCircle, Info } from 'lucide-react';
import { useDocumentValidation, type FieldErrors } from '@/hooks/useDocumentValidation';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

// ─── Types ──────────────────────────────────────────────────────────────────

interface FamiliaDef {
  id: string;
  label: string;
  icon: React.ElementType;
  tipos: { value: string; label: string }[];
}

interface CreateDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contratoId: string;
  familias: FamiliaDef[];
  uploading: boolean;
  onSubmit: (data: {
    titulo: string;
    familia: string;
    tipo: string;
    descricao: string;
    file: File;
  }) => void;
}

// ─── Component ──────────────────────────────────────────────────────────────

export const CreateDocumentDialog: React.FC<CreateDocumentDialogProps> = ({
  open,
  onOpenChange,
  contratoId,
  familias,
  uploading,
  onSubmit,
}) => {
  const [titulo, setTitulo] = useState('');
  const [familia, setFamilia] = useState('tecnico');
  const [tipo, setTipo] = useState('tecnico_projeto');
  const [descricao, setDescricao] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);

  const { validateForm, checkDuplicate } = useDocumentValidation(contratoId);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setTitulo('');
      setFamilia('tecnico');
      setTipo('tecnico_projeto');
      setDescricao('');
      setFile(null);
      setErrors({});
      setDuplicateWarning(null);
    }
  }, [open]);

  // Check duplicate when file or tipo changes
  useEffect(() => {
    if (!file || !tipo) {
      setDuplicateWarning(null);
      return;
    }

    let cancelled = false;
    const check = async () => {
      setCheckingDuplicate(true);
      const result = await checkDuplicate(file.name, tipo);
      if (!cancelled) {
        setDuplicateWarning(
          result.isDuplicate
            ? `Já existe "${result.existingDocTitle}" com mesmo arquivo e tipo. Considere enviar como nova versão.`
            : null
        );
        setCheckingDuplicate(false);
      }
    };
    check();
    return () => { cancelled = true; };
  }, [file, tipo, checkDuplicate]);

  const handleSubmit = useCallback(() => {
    const validationErrors = validateForm({
      titulo,
      familia,
      tipo,
      descricao,
      arquivo: file,
    });

    if (validationErrors) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    onSubmit({ titulo, familia, tipo, descricao, file: file! });
  }, [titulo, familia, tipo, descricao, file, validateForm, onSubmit]);

  const handleFamiliaChange = (v: string) => {
    setFamilia(v);
    const fam = familias.find(f => f.id === v);
    if (fam && fam.tipos.length > 0) setTipo(fam.tipos[0].value);
    setErrors(prev => ({ ...prev, familia: undefined }));
  };

  const FieldError = ({ field }: { field: keyof FieldErrors }) =>
    errors[field] ? (
      <p className="text-xs text-destructive mt-1 flex items-center gap-1">
        <AlertCircle className="h-3 w-3" /> {errors[field]}
      </p>
    ) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Novo Documento</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>
              Título *
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3 w-3 text-muted-foreground inline ml-1" />
                  </TooltipTrigger>
                  <TooltipContent>Nome descritivo do documento (ex: "Planta Baixa - Bloco A")</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <Input
              value={titulo}
              onChange={e => { setTitulo(e.target.value); setErrors(prev => ({ ...prev, titulo: undefined })); }}
              placeholder="Nome do documento"
              maxLength={200}
              className={errors.titulo ? 'border-destructive' : ''}
            />
            <FieldError field="titulo" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Família *</Label>
              <Select value={familia} onValueChange={handleFamiliaChange}>
                <SelectTrigger className={errors.familia ? 'border-destructive' : ''}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {familias.filter(f => f.id !== 'databook').map(f => (
                    <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError field="familia" />
            </div>
            <div>
              <Label>Tipo *</Label>
              <Select value={tipo} onValueChange={v => { setTipo(v); setErrors(prev => ({ ...prev, tipo: undefined })); }}>
                <SelectTrigger className={errors.tipo ? 'border-destructive' : ''}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(familias.find(f => f.id === familia)?.tipos || []).map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError field="tipo" />
            </div>
          </div>
          <div>
            <Label>Descrição</Label>
            <Textarea
              value={descricao}
              onChange={e => { setDescricao(e.target.value); setErrors(prev => ({ ...prev, descricao: undefined })); }}
              rows={2}
              maxLength={2000}
              className={errors.descricao ? 'border-destructive' : ''}
            />
            <FieldError field="descricao" />
          </div>
          <div>
            <Label>
              Arquivo *
              <span className="text-xs text-muted-foreground ml-2">(máx. 100 MB)</span>
            </Label>
            <Input
              type="file"
              onChange={e => { setFile(e.target.files?.[0] || null); setErrors(prev => ({ ...prev, arquivo: undefined })); }}
              className={errors.arquivo ? 'border-destructive' : ''}
            />
            <FieldError field="arquivo" />
            {/* Duplicate warning */}
            {duplicateWarning && (
              <div className="mt-2 p-2 rounded-md bg-warning/10 border border-warning/30 text-xs text-warning flex items-start gap-1.5">
                <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>{duplicateWarning}</span>
              </div>
            )}
            {checkingDuplicate && (
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> Verificando duplicatas...
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={uploading || !titulo.trim() || !file}>
            {uploading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Enviando...</> : 'Criar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
